import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
  BarChart,
  Bar
} from 'recharts';
import { generateMaintenanceData } from '../../utils/generateMaintenanceData';
import type { Asset, MaintenanceRecord } from '@/types';
import { WrenchScrewdriverIcon, ExclamationTriangleIcon, CogIcon, ChartBarIcon } from '@heroicons/react/24/outline';

interface HealthScore {
  id: string;
  name: string;
  score: number;
}

export function PredictiveDashboard() {
  // Generate sample data
  const { assets, maintenanceRecords, predictedFailures, sparePartsForecasts } = generateMaintenanceData();

  // Calculate health scores (0-100)
  const assetHealthScores: HealthScore[] = assets.map((asset: Asset) => {
    const assetRecords = maintenanceRecords.filter((r: MaintenanceRecord) => r.assetId === asset.id);
    const lastMaintenance = new Date(asset.lastMaintenanceDate).getTime();
    const daysSinceLastMaintenance = Math.floor((Date.now() - lastMaintenance) / (1000 * 60 * 60 * 24));
    const recentFailures = assetRecords.filter((r: MaintenanceRecord) => r.failureCategory).length;
    
    // Simple health score calculation
    let healthScore = 100;
    healthScore -= daysSinceLastMaintenance * 0.1; // Reduce score based on days since maintenance
    healthScore -= recentFailures * 5; // Reduce score for each failure
    healthScore = Math.max(0, Math.min(100, healthScore)); // Clamp between 0-100
    
    return {
      id: asset.id,
      name: asset.name,
      score: healthScore
    };
  });

  // Summary stats
  const stats = {
    averageHealth: Math.round(assetHealthScores.reduce((sum: number, asset: HealthScore) => sum + asset.score, 0) / assetHealthScores.length),
    predictedFailures: predictedFailures.length,
    criticalAssets: assetHealthScores.filter((a: HealthScore) => a.score < 60).length,
    sparePartsNeeded: sparePartsForecasts.filter((f: { stockLevel: number; recommendedLevel: number }) => f.stockLevel < f.recommendedLevel).length
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Predictive Maintenance & Spare Parts</h1>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900">
              <CogIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium dark:text-white">Average Health</h3>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.averageHealth}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium dark:text-white">Predicted Failures</h3>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.predictedFailures}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900">
              <WrenchScrewdriverIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium dark:text-white">Critical Assets</h3>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.criticalAssets}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900">
              <ChartBarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium dark:text-white">Parts to Order</h3>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.sparePartsNeeded}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Equipment Health Scores */}
      <Card>
        <CardHeader>
          <CardTitle>Equipment Health Scores</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={assetHealthScores}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="score" fill="#8884d8">
                  {assetHealthScores.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.score > 80 ? '#4ade80' : entry.score > 60 ? '#facc15' : '#ef4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Predicted Failures */}
      <Card>
        <CardHeader>
          <CardTitle>Predicted Failures (Next 3 Months)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="predictedDate" 
                  domain={['auto', 'auto']}
                  tickFormatter={(unixTime) => new Date(unixTime).toLocaleDateString()}
                  type="number"
                />
                <YAxis dataKey="confidence" domain={[0, 100]} />
                <ZAxis dataKey="impact" range={[50, 400]} />
                <Tooltip 
                  formatter={(value: any, name: string) => {
                    if (name === 'predictedDate') return new Date(value).toLocaleDateString();
                    return value;
                  }}
                />
                <Scatter 
                  data={predictedFailures} 
                  fill="#8884d8"
                  shape="circle"
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Spare Parts Forecast */}
      <Card>
        <CardHeader>
          <CardTitle>Spare Parts Stock Level Forecast</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparePartsForecasts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="partName" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="stockLevel" 
                  stroke="#8884d8" 
                  name="Current Stock"
                />
                <Line 
                  type="monotone" 
                  dataKey="recommendedLevel" 
                  stroke="#82ca9d" 
                  name="Recommended Level"
                />
                <Line 
                  type="monotone" 
                  dataKey="predictedDemand" 
                  stroke="#ffc658" 
                  name="Predicted Demand"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Service Intervals */}
      <Card>
        <CardHeader>
          <CardTitle>Service Intervals Based on Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={maintenanceRecords}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="startDate" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value: any, name: string) => {
                    if (name === 'startDate') return new Date(value).toLocaleDateString();
                    return value;
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="downtime" 
                  stroke="#8884d8" 
                  name="Downtime (hours)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 